import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Home, Save } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { planMemberEdit, buildMemberFromForm, buildNormalizedFields } from '../../utils/memberEdit';

const emptyFormData = {
  apartmentNumber: '',
  address: '',
  zipCode: '',
  city: '',
  firstName: '',
  lastName: '',
  legalEntity: '',
  email: '',
  phone: '',
};

export default function EditApartmentModal({ open, onClose, apartment, onSuccess, mode = 'edit', associationId }) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);

  const isAddMode = mode === 'add';

  // Populate form when apartment changes (edit mode) or reset (add mode)
  useEffect(() => {
    if (isAddMode) {
      setFormData(emptyFormData);
    } else if (apartment) {
      const idx = apartment.memberIndex ?? 0;
      const member = apartment.members?.[idx] || {};
      setFormData({
        apartmentNumber: apartment.apartmentNumber || '',
        address: apartment.address || '',
        zipCode: apartment.zipCode || '',
        city: apartment.city || '',
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        legalEntity: member.legalEntity || '',
        email: member.email || '',
        phone: member.phone || '',
      });
    }
  }, [apartment, isAddMode, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    // Validate: need at least name OR legal entity
    const hasName = formData.firstName.trim() && formData.lastName.trim();
    const hasLegalEntity = formData.legalEntity.trim();

    if (!hasName && !hasLegalEntity) {
      alert('Ange antingen förnamn + efternamn eller juridisk person');
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();

      if (isAddMode) {
        // Föreningens NAMN måste skrivas, inte bara id:t: vyerna visar
        // "Okänd förening" när housingCooperative saknas, och medlemmen fastnar
        // när hen sedan gör anspråk på lägenheten.
        let housingCooperative = '';
        if (associationId) {
          const assocSnap = await getDoc(doc(db, 'associations', associationId));
          housingCooperative = assocSnap.exists() ? (assocSnap.data().name || '') : '';
        }

        await addDoc(collection(db, 'housing'), {
          apartmentNumber: formData.apartmentNumber,
          address: formData.address,
          zipCode: formData.zipCode,
          city: formData.city,
          members: [buildMemberFromForm(formData)],
          ...buildNormalizedFields(formData),
          associationId,
          ...(housingCooperative && { housingCooperative }),
          hasMemberInfo: true,
          createdAt: now,
          updatedAt: now,
          ownedBy: null,
        });
      } else {
        const housingRef = doc(db, 'housing', apartment.id);
        // Läs färskaste dokumentet (associationId saknas i den utplattade raden
        // och vi vill undvika att skriva över andra medlemmar pga inaktuell data).
        const snap = await getDoc(housingRef);
        const docData = snap.exists()
          ? snap.data()
          : { ...apartment, associationId: apartment.associationId || associationId };
        if (!docData.associationId) {
          docData.associationId = apartment.associationId || associationId;
        }

        const plan = planMemberEdit({
          docData,
          memberIndex: apartment.memberIndex ?? 0,
          formData,
        });

        if (plan.action === 'split') {
          // Bryt ut den redigerade medlemmen till ett eget housing-dokument med
          // det nya lägenhetsnumret, och ta bort hen ur originalet. Övriga
          // medlemmars lägenhetsnummer lämnas orört.
          await addDoc(collection(db, 'housing'), {
            ...plan.newDoc,
            createdAt: now,
            updatedAt: now,
          });
          await updateDoc(housingRef, {
            ...plan.originalUpdate,
            updatedAt: now,
          });
        } else {
          await updateDoc(housingRef, {
            ...plan.update,
            updatedAt: now,
          });
        }
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating apartment:', error);
      alert(isAddMode ? 'Kunde inte lägga till medlem' : 'Kunde inte spara ändringar');
    } finally {
      setSaving(false);
    }
  };

  if (!isAddMode && !apartment) return null;

  return (
    <Dialog open={open} onClose={onClose} className="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500"
          onClick={onClose}
          disabled={saving}
          aria-label="Stäng"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Home className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
              {isAddMode ? 'Lägg till medlem' : 'Redigera lägenhet'}
            </h2>
            <p className="text-sm text-[#626262] mt-1">
              {isAddMode ? 'Fyll i information om lägenheten och dess ägare' : 'Uppdatera information om lägenheten och dess ägare'}
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Lägenhetsinformation */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Lägenhetsinformation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lägenhetsnummer *
                </label>
                <input
                  type="text"
                  name="apartmentNumber"
                  value={formData.apartmentNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. 1204"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adress *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. Storgatan 1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postnummer
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. 123 45"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ort
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. Stockholm"
                />
              </div>
            </div>
          </div>

          {/* Ägarinformation */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Ägarinformation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Förnamn
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. Anna"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Efternamn
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. Andersson"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Juridisk person (om företag/organisation)
                </label>
                <input
                  type="text"
                  name="legalEntity"
                  value={formData.legalEntity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. Företag AB"
                />
              </div>
            </div>
          </div>

          {/* Kontaktuppgifter */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Kontaktuppgifter</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-post
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. anna@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="t.ex. 070-123 45 67"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Validation hint */}
        <p className="text-xs text-gray-500 mt-4">
          * Ange antingen förnamn + efternamn eller juridisk person
        </p>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-3 text-sm font-medium text-zinc-700 bg-zinc-100 rounded-xl hover:bg-zinc-200 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 text-sm font-medium text-white bg-[#62B8E9] rounded-xl hover:bg-[#4A9FD1] transition-colors flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Sparar...' : (isAddMode ? 'Lägg till' : 'Spara ändringar')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
